import { useRef } from 'react'
import { useI18n } from '@/lib/i18n'
import { BgVideo, PhraseSwap, clamp01, rand, useCanvasLoop, useCtaText, useFrame, usePhraseCycle, type SaverProps } from './shared'

const CYCLE = 5.2
const PRE_FLASH = 0.0 // small metering pre-flash
const MAIN_FLASH = 0.2
const CHARGED_AT = 4.4

interface Mote { x: number; y: number; z: number; vx: number; vy: number; phase: number }

/** Light from the flash events up to `c` seconds into the cycle. */
function lightAt(c: number, withPre: boolean, decay: number) {
  let l = 0
  if (withPre && c >= PRE_FLASH) l += 0.22 * Math.exp(-(c - PRE_FLASH) * decay * 2.5)
  if (c >= MAIN_FLASH) l += Math.exp(-(c - MAIN_FLASH) * decay)
  return l
}

export default function FlashSaver({ videoSrc, phrases, intervalSeconds, wakeMethod }: SaverProps) {
  const { t } = useI18n()
  const cta = useCtaText(wakeMethod)
  const phrase = usePhraseCycle(phrases, Math.max(CYCLE, intervalSeconds * 2))
  const videoRef = useRef<HTMLVideoElement>(null)
  const darkRef = useRef<HTMLDivElement>(null)
  const whiteRef = useRef<HTMLDivElement>(null)
  const flareRef = useRef<HTMLDivElement>(null)
  const litRef = useRef<HTMLDivElement>(null)
  const burnRef = useRef<HTMLDivElement>(null)
  const ringRef = useRef<SVGCircleElement>(null)
  const ledRef = useRef<HTMLSpanElement>(null)
  const ledTextRef = useRef<HTMLSpanElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const lightRef = useRef(0)
  const motesRef = useRef<Mote[]>([])

  useFrame((time) => {
    const cycleNo = Math.floor((time + 2.5) / CYCLE)
    const c = (time + 2.5) % CYCLE
    const withPre = cycleNo % 3 !== 2 // every third shot skips the pre-flash
    const light = lightAt(c, withPre, 1.5)
    const burst = lightAt(c, withPre, 16)
    const burn = c >= MAIN_FLASH ? Math.exp(-(c - MAIN_FLASH) * 0.6) : 0
    lightRef.current = light
    if (videoRef.current) {
      videoRef.current.style.filter = `brightness(${(0.1 + light * 1.5).toFixed(3)}) contrast(1.18) saturate(${(0.45 + light * 0.75).toFixed(3)})`
    }
    if (darkRef.current) darkRef.current.style.opacity = String(clamp01(1 - light * 0.8))
    if (whiteRef.current) whiteRef.current.style.opacity = String(clamp01(burst * 0.85))
    if (flareRef.current) {
      flareRef.current.style.opacity = String(clamp01(burst * 1.4))
      flareRef.current.style.transform = `translate(-50%, -50%) scale(${(0.6 + burst * 0.6).toFixed(3)})`
    }
    if (litRef.current) litRef.current.style.opacity = String(clamp01(light * 1.15))
    if (burnRef.current) burnRef.current.style.opacity = String(clamp01(burn * 0.85 - light * 0.4))
    const charge = clamp01((c - 1.0) / (CHARGED_AT - 1.0))
    const ready = c >= CHARGED_AT
    if (ringRef.current) ringRef.current.style.strokeDashoffset = String(100 - charge * 100)
    if (ledRef.current) {
      ledRef.current.style.background = ready ? '#4ade80' : '#f59e0b'
      ledRef.current.style.boxShadow = ready ? '0 0 12px #4ade80' : `0 0 ${(4 + Math.sin(time * 8) * 4).toFixed(1)}px #f59e0b`
    }
    if (ledTextRef.current) ledTextRef.current.textContent = ready ? 'READY' : 'CHARGING'
  })

  // Dust hanging in the air, only visible while the flash lights it
  useCanvasLoop(canvasRef, (ctx, w, h, time, dt) => {
    const motes = motesRef.current
    if (motes.length === 0) {
      for (let i = 0; i < 140; i++) motes.push({ x: rand(0, w), y: rand(0, h), z: rand(0.2, 1), vx: rand(-6, 6), vy: rand(-4, 7), phase: rand(0, 6.28) })
    }
    const light = lightRef.current
    for (const m of motes) {
      m.x += (m.vx + Math.sin(time * 0.6 + m.phase) * 5) * dt * m.z
      m.y += m.vy * dt * m.z
      if (m.x < -10) m.x = w + 10
      if (m.x > w + 10) m.x = -10
      if (m.y > h + 10) m.y = -10
      if (m.y < -10) m.y = h + 10
      const a = clamp01(0.03 + light * 0.75 * m.z)
      if (a < 0.02) continue
      const r = 0.6 + m.z * m.z * 2.6
      ctx.fillStyle = `rgba(255, 248, 235, ${a.toFixed(3)})`
      ctx.beginPath()
      ctx.arc(m.x, m.y, r, 0, Math.PI * 2)
      ctx.fill()
    }
  })

  const hero = t('screensaver.hero.flash')
  const heroClass = 'absolute inset-x-0 top-1/2 -translate-y-1/2 px-[6vw] text-center text-[10vmin] font-black uppercase leading-[0.95] tracking-tight'

  return (
    <div className="absolute inset-0 overflow-hidden bg-black" style={{ fontFamily: "'Geist Variable', sans-serif" }}>
      <BgVideo ref={videoRef} src={videoSrc} style={{ filter: 'brightness(0.1)' }} />
      {/* Falloff: the flash lights the middle of the room more than the edges */}
      <div ref={darkRef} className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 42%, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.92) 70%, #000 100%)' }} />
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />

      {/* Hero text: barely visible in the dark, blown out by the flash, then an afterimage */}
      <div className={heroClass} style={{ color: 'rgba(255,255,255,0.075)' }}>{hero}</div>
      <div ref={burnRef} className={heroClass} style={{ opacity: 0, color: 'rgba(110, 220, 255, 0.55)', filter: 'blur(5px)', textShadow: '0 0 30px rgba(236, 72, 153, 0.8)' }}>{hero}</div>
      <div ref={litRef} className={heroClass} style={{ opacity: 0, color: '#fff', textShadow: '0 0 40px rgba(255,255,255,0.6)' }}>{hero}</div>

      <div className="absolute inset-x-0 top-[calc(50%+13vmin)] px-[8vw] text-center">
        <PhraseSwap phrase={phrase} className="text-[3.6vmin] font-medium text-white/60" />
      </div>

      {/* Flash bulb, star burst and anamorphic streak */}
      <div ref={flareRef} className="pointer-events-none absolute left-1/2 top-[14%]" style={{ opacity: 0, mixBlendMode: 'screen' }}>
        <div className="absolute left-1/2 top-1/2 h-[70vmin] w-[70vmin] -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(220,235,255,0.65) 12%, rgba(160,190,255,0.12) 40%, transparent 65%)' }} />
        <div className="absolute left-1/2 top-1/2 h-[1.2vmin] w-[220vw] -translate-x-1/2 -translate-y-1/2" style={{ background: 'linear-gradient(90deg, transparent, rgba(120,170,255,0.55) 30%, #fff 50%, rgba(120,170,255,0.55) 70%, transparent)', filter: 'blur(2px)' }} />
        {[0, 60, 120].map((deg) => (
          <div key={deg} className="absolute left-1/2 top-1/2 h-[0.5vmin] w-[48vmin]" style={{ transform: `translate(-50%, -50%) rotate(${deg + 30}deg)`, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.9), transparent)' }} />
        ))}
      </div>

      <div ref={whiteRef} className="pointer-events-none absolute inset-0 bg-white" style={{ opacity: 0 }} />

      {/* Flash status, like the back of a speedlight */}
      <div className="pointer-events-none absolute inset-x-0 bottom-[5vmin] flex flex-col items-center gap-[2vmin]">
        <div className="text-[2.6vmin] font-semibold tracking-wide text-white/85">{cta}</div>
        <div className="flex items-center gap-[1.6vmin] font-mono text-[1.7vmin] tracking-[0.3em] text-white/55">
          <svg viewBox="0 0 40 40" className="h-[4vmin] w-[4vmin] -rotate-90">
            <circle cx="20" cy="20" r="15.9" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
            <circle ref={ringRef} cx="20" cy="20" r="15.9" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="3" pathLength={100} strokeDasharray="100" strokeDashoffset="100" strokeLinecap="round" />
          </svg>
          <span ref={ledRef} className="h-[1.2vmin] w-[1.2vmin] rounded-full" />
          <span ref={ledTextRef}>CHARGING</span>
        </div>
      </div>
    </div>
  )
}
